import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, CheckCircle, ChevronDown, CreditCard, Loader2, ShieldCheck, Star } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;
const LIMIT_LABELS = [
  ['admin_users_limit', 'Staff access seats'],
  ['workers_limit', 'Workers'],
  ['service_users_limit', 'Service users'],
  ['client_organisations_limit', 'Client organisations'],
  ['applicant_invites_monthly_limit', 'Applicant invitations/month'],
  ['storage_gb_limit', 'Storage GB'],
  ['evidence_pack_exports_limit', 'Evidence exports'],
];

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatLimit(value) {
  if (value === undefined || value === null || value === 0) return 'Not included';
  return Number(value).toLocaleString();
}

function formatUsage(value) {
  if (value === undefined || value === null) return 'Unavailable';
  return Number(value).toLocaleString();
}

function formatAllowance(value) {
  if (value === undefined || value === null) return 'Not available';
  if (value === 0) return 'Not included';
  return Number(value).toLocaleString();
}

function hasFiniteNumber(value) {
  return value !== undefined && value !== null && Number.isFinite(Number(value));
}

function formatPence(amount, suffix = '') {
  if (typeof amount !== 'number') return 'Contact CareOps';
  const formatted = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount / 100);
  return `${formatted}${suffix}`;
}

function formatDate(seconds) {
  if (!seconds) return 'Not scheduled';
  return new Date(seconds * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function statusCopy(subscription) {
  if (subscription?.is_private_beta) return 'Private beta';
  if (subscription?.cancel_at_period_end) return 'Cancellation scheduled';
  if (subscription?.payment_attention) return 'Payment needs attention';
  if (subscription?.stripe_status === 'active' || subscription?.stripe_status === 'trialing') return 'Subscription confirmed';
  if (subscription?.stripe_status === 'incomplete') return 'Payment is still processing';
  if (subscription?.subscription_status === 'canceled') return 'Subscription has ended';
  return subscription?.subscription_status || 'Not started';
}

function stateBadge(state) {
  const tone = {
    Included: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    'Available as add-on': 'border-blue-200 bg-blue-50 text-blue-700',
    Beta: 'border-amber-200 bg-amber-50 text-amber-700',
    'Coming soon': 'border-slate-200 bg-slate-50 text-slate-600',
  }[state] || 'border-slate-200 bg-white text-slate-500';
  return <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}>{state}</span>;
}

export default function PlansBillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [billing, setBilling] = useState(null);
  const [error, setError] = useState('');
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [expanded, setExpanded] = useState({ 'Workforce & Recruitment': true });
  const checkoutReturned = searchParams.get('checkout');
  const [checkoutConfirmation, setCheckoutConfirmation] = useState(checkoutReturned === 'success' ? 'pending' : '');
  const [invoices, setInvoices] = useState([]);
  const [invoiceError, setInvoiceError] = useState('');

  const loadBilling = async () => {
    try {
      setError('');
      const response = await axios.get(`${API}/billing/summary`, { headers: authHeaders() });
      setBilling(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Billing is temporarily unavailable');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBilling();
  }, []);

  useEffect(() => {
    if (checkoutReturned !== 'success') return undefined;
    let attempts = 0;
    const timer = window.setInterval(async () => {
      attempts += 1;
      try {
        const response = await axios.get(`${API}/billing/checkout-status`, { headers: authHeaders() });
        setBilling((current) => (current ? { ...current, subscription: response.data.subscription } : current));
        const next = response.data.subscription || {};
        if (next.stripe_status === 'active' || next.stripe_status === 'trialing') {
          setCheckoutConfirmation('confirmed');
          window.clearInterval(timer);
        } else if (next.payment_attention || ['past_due', 'canceled', 'unpaid', 'incomplete_expired'].includes(next.stripe_status)) {
          setCheckoutConfirmation('failed');
          window.clearInterval(timer);
        }
      } catch {
        // Browser return state never activates a plan; the webhook-confirmed summary is polled quietly.
      }
      if (attempts >= 5) {
        setCheckoutConfirmation((current) => (current === 'pending' ? 'unconfirmed' : current));
        window.clearInterval(timer);
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [checkoutReturned]);

  const catalogue = billing?.commercial_catalogue || {};
  const plans = billing?.plans?.length ? billing.plans : (catalogue.plans || []);
  const subscription = billing?.subscription || {};
  const currentPlan = subscription.plan_id || catalogue.current_plan_id || 'essential';
  const usage = billing?.usage || {};
  const privateBeta = subscription.is_private_beta;
  const checkoutEnabled = Boolean(billing?.checkout_enabled);
  const comparison = catalogue.comparison || {};
  const categories = catalogue.categories || Object.keys(comparison);
  const currentPlanData = plans.find((plan) => plan.id === currentPlan);
  const supportsAnnual = plans.some((plan) => plan.prices?.monthly?.active && plan.prices?.annual?.active);
  const annualNote = catalogue.annual_saving_note || 'Annual pricing includes two months compared with monthly billing.';

  const invoiceLoadAllowed = Boolean(subscription.billing_provider && !privateBeta);
  const visibleAddons = useMemo(() => catalogue.addons || [], [catalogue.addons]);
  const hasExistingStripeSubscription = Boolean(subscription.billing_provider === 'stripe' && subscription.stripe_status && ['active', 'trialing', 'past_due', 'incomplete'].includes(subscription.stripe_status));
  const overAllowanceCopy = privateBeta
    ? 'Existing records remain available during private beta; this does not delete or automatically restrict records.'
    : 'Existing records remain available while you review allowance options.';

  useEffect(() => {
    if (!invoiceLoadAllowed) return;
    axios.get(`${API}/billing/invoices`, { headers: authHeaders() })
      .then((response) => setInvoices(response.data?.invoices || []))
      .catch((err) => setInvoiceError(err.response?.data?.detail || 'Invoices are temporarily unavailable'));
  }, [invoiceLoadAllowed]);

  const startCheckout = async (planId) => {
    try {
      setActionLoading(planId);
      const response = await axios.post(
        `${API}/billing/checkout-session`,
        { plan_id: planId, billing_interval: billingInterval },
        { headers: authHeaders() },
      );
      if (response.data?.url) window.location.assign(response.data.url);
      else toast.error('Checkout session could not be created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout session could not be created');
    } finally {
      setActionLoading('');
    }
  };

  const manageBilling = async () => {
    try {
      setActionLoading('portal');
      const response = await axios.post(`${API}/billing/customer-portal-session`, {}, { headers: authHeaders() });
      if (response.data?.url) window.location.assign(response.data.url);
      else toast.error('Billing portal is temporarily unavailable');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Billing portal is temporarily unavailable');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-amber-200 bg-amber-50 p-6">
        <h1 className="text-2xl font-bold text-slate-900">Plans & Billing</h1>
        <p className="mt-3 text-sm text-amber-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Plans & Billing</h1>
          <p className="mt-1 text-sm text-slate-600">Compare CareOps plans and manage verified Stripe billing.</p>
        </div>
        {supportsAnnual ? (
          <div>
            <div className="inline-flex w-fit rounded-lg border border-slate-200 bg-white p-1" aria-label="Billing interval">
              {['monthly', 'annual'].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize ${billingInterval === value ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                  onClick={() => setBillingInterval(value)}
                >
                  {value}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-slate-500">{annualNote}</p>
          </div>
        ) : null}
      </div>

      {checkoutConfirmation === 'pending' ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">Confirming your subscription securely...</div>
      ) : null}
      {checkoutConfirmation === 'confirmed' ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">Subscription confirmed.</div>
      ) : null}
      {checkoutConfirmation === 'failed' ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">Payment needs attention.</div>
      ) : null}
      {checkoutConfirmation === 'unconfirmed' ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">We could not confirm the subscription yet.</div>
      ) : null}
      {checkoutReturned === 'cancelled' ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">Checkout was cancelled.</div>
      ) : null}

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            Current subscription
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-[1fr_1.6fr]">
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
            <p className="text-xs font-semibold uppercase text-primary">Your current plan</p>
            <p className="mt-1 text-3xl font-semibold text-slate-900">{currentPlanData?.name || 'Essential'}</p>
            <p className="mt-2 text-sm text-slate-700">{privateBeta ? 'Billing managed by CareOps' : statusCopy(subscription)}</p>
            {subscription.payment_attention ? (
              <p className="mt-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                Payment needs attention. Manage billing to update the payment method.
              </p>
            ) : null}
            {subscription.cancel_at_period_end ? <p className="mt-3 text-sm text-amber-800">Access continues until {formatDate(subscription.current_period_end)}.</p> : null}
            <div className="mt-4 flex flex-wrap gap-2">
              <Badge variant="outline">{statusCopy(subscription)}</Badge>
              {subscription.billing_interval ? <Badge variant="outline" className="capitalize">{subscription.billing_interval}</Badge> : null}
              {privateBeta ? <Badge variant="outline">Billing managed by CareOps</Badge> : null}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[
              ['Staff seats used', usage.staff_seats, currentPlanData?.limits?.admin_users_limit],
              ['Workers used', usage.workers, currentPlanData?.limits?.workers_limit],
              ['Service users used', usage.service_users, currentPlanData?.limits?.service_users_limit],
              ['Client organisations used', usage.client_organisations, currentPlanData?.limits?.client_organisations_limit],
              ['Storage allowance', null, currentPlanData?.limits?.storage_gb_limit],
              ['Evidence exports', null, currentPlanData?.limits?.evidence_pack_exports_limit],
              ['Applicant invitations/month', null, currentPlanData?.limits?.applicant_invites_monthly_limit],
              ['Payment status', null, statusCopy(subscription)],
            ].map(([label, used, included]) => {
              const isUsageMetric = used !== null;
              const numericUsed = Number(used);
              const numericIncluded = Number(included);
              const hasNumericUsage = isUsageMetric && hasFiniteNumber(used);
              const hasNumericAllowance = hasFiniteNumber(included);
              const isOverAllowance = hasNumericUsage && hasNumericAllowance && numericUsed > numericIncluded;
              const displayValue = isUsageMetric
                ? `${formatUsage(used)} / ${formatAllowance(included)}`
                : typeof included === 'number'
                  ? formatAllowance(included)
                  : String(included || 'Not available');
              return (
                <div key={label} className={`rounded-lg border bg-white p-3 ${isOverAllowance ? 'border-amber-200 bg-amber-50/60' : 'border-slate-200'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs uppercase text-slate-500">{label}</p>
                    {isOverAllowance ? <Badge variant="outline" className="border-amber-300 bg-amber-100 text-amber-800">Over allowance</Badge> : null}
                  </div>
                  <p className="mt-1 text-lg font-semibold text-slate-900">{displayValue}</p>
                  {isOverAllowance ? <p className="mt-2 text-xs leading-relaxed text-amber-800">{overAllowanceCopy}</p> : null}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-3">
        {plans.map((plan) => {
          const priceAmount = billingInterval === 'annual' ? plan.annual_amount : plan.monthly_amount;
          const price = plan.prices?.[billingInterval];
          const priceAvailable = Boolean(price?.active);
          const isCurrent = plan.id === currentPlan;
          const unavailable = privateBeta || !checkoutEnabled || !priceAvailable;
          return (
            <Card key={plan.id} className={`relative border-slate-200 ${isCurrent ? 'ring-2 ring-primary/40' : ''} ${plan.most_popular ? 'shadow-md' : ''}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle>{plan.name}</CardTitle>
                    <p className="text-sm font-medium text-primary">{plan.positioning}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {plan.most_popular ? <Badge className="bg-primary text-white"><Star className="mr-1 h-3 w-3" />Most popular</Badge> : null}
                    {isCurrent ? <Badge variant="outline">Your current plan</Badge> : null}
                  </div>
                </div>
                <p className="min-h-[44px] text-sm text-slate-600">{plan.description}</p>
                <p className="text-3xl font-semibold text-slate-900">
                  {plan.enterprise_from_price && billingInterval === 'monthly' ? 'From ' : ''}{formatPence(priceAmount, billingInterval === 'annual' ? '/year' : '/month')}
                </p>
                <p className="text-xs text-slate-500">Excluding VAT</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-2">
                  {LIMIT_LABELS.map(([key, label]) => (
                    <div key={key} className="flex justify-between gap-3 text-sm">
                      <span className="text-slate-600">{label}</span>
                      <span className="font-medium text-slate-900">{formatLimit(plan.limits?.[key])}</span>
                    </div>
                  ))}
                </div>
                {isCurrent ? (
                  <Button className="w-full" disabled>Current plan</Button>
                ) : hasExistingStripeSubscription ? (
                  <Button className="w-full" variant="outline" onClick={manageBilling} disabled={actionLoading === 'portal'}>
                    Change in billing portal
                  </Button>
                ) : !privateBeta && !checkoutEnabled ? (
                  <div className="grid gap-2">
                    <Button className="w-full" variant="outline" disabled>Online purchasing is not enabled yet</Button>
                    <Button type="button" className="w-full" variant="outline" onClick={() => navigate('/contact?type=platform_demo')}>
                      Contact CareOps
                    </Button>
                  </div>
                ) : unavailable ? (
                  <Button className="w-full" variant="outline" disabled>{privateBeta ? 'Contact CareOps' : 'Billing setup unavailable'}</Button>
                ) : (
                  <Button className="w-full bg-primary text-white hover:bg-primary-hover" onClick={() => startCheckout(plan.id)} disabled={actionLoading === plan.id}>
                    {actionLoading === plan.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                    {plan.id === 'enterprise' ? 'Upgrade securely' : 'Upgrade securely'}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Full comparison</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {categories.map((category) => (
            <div key={category} className="rounded-lg border border-slate-200">
              <button
                type="button"
                className="flex w-full items-center justify-between px-4 py-3 text-left font-semibold text-slate-900"
                onClick={() => setExpanded((current) => ({ ...current, [category]: !current[category] }))}
              >
                {category}
                <ChevronDown className={`h-4 w-4 transition-transform ${expanded[category] ? 'rotate-180' : ''}`} />
              </button>
              {expanded[category] ? (
                <div className="overflow-x-auto border-t border-slate-200">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                        <th className="px-4 py-2">Feature</th>
                        {plans.map((plan) => <th key={plan.id} className="px-4 py-2">{plan.name}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {(comparison[category] || []).map((feature) => (
                        <tr key={feature.id} className="border-t border-slate-100">
                          <td className="px-4 py-3 font-medium text-slate-800">{feature.name}</td>
                          {plans.map((plan) => <td key={plan.id} className="px-4 py-3">{stateBadge(feature.states?.[plan.id])}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Add-ons</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visibleAddons.map((addon) => (
            <div key={addon.id} className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{addon.name}</p>
                  <p className="mt-1 text-sm text-slate-600">{formatPence(addon.price_monthly, '/month')}</p>
                </div>
                {stateBadge(addon.status === 'coming_soon' ? 'Coming soon' : addon.status === 'beta' ? 'Beta' : 'Available as add-on')}
              </div>
              <Button type="button" variant="outline" className="mt-4 w-full" onClick={() => navigate('/contact?type=platform_demo')}>
                Contact CareOps
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Invoices and billing actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" onClick={manageBilling} disabled={privateBeta || !invoiceLoadAllowed || actionLoading === 'portal'}>
            {actionLoading === 'portal' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
            Manage billing
          </Button>
          <p className="text-sm text-slate-600">
            {privateBeta ? 'Private beta: Billing managed by CareOps.' : 'Invoices are available through Stripe-hosted billing when a verified customer exists.'}
          </p>
          <CheckCircle className="h-4 w-4 text-primary" />
          {invoiceError ? <p className="basis-full text-sm text-amber-700">{invoiceError}</p> : null}
          {invoiceLoadAllowed && invoices.length ? (
            <div className="basis-full overflow-x-auto">
              <table className="mt-3 w-full min-w-[680px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                    <th className="py-2 pr-4">Invoice</th>
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Amount</th>
                    <th className="py-2 pr-4">Links</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-slate-100">
                      <td className="py-2 pr-4 font-medium text-slate-900">{invoice.number || invoice.id}</td>
                      <td className="py-2 pr-4 text-slate-600">{formatDate(invoice.created)}</td>
                      <td className="py-2 pr-4 text-slate-600">{invoice.status || 'Unknown'}</td>
                      <td className="py-2 pr-4 text-slate-600">{formatPence((invoice.amount_paid || invoice.amount_due || 0), '')}</td>
                      <td className="py-2 pr-4">
                        <div className="flex gap-3">
                          {invoice.hosted_invoice_url ? <a className="text-primary underline" href={invoice.hosted_invoice_url}>Hosted invoice</a> : null}
                          {invoice.invoice_pdf ? <a className="text-primary underline" href={invoice.invoice_pdf}>PDF</a> : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
